import { useEffect, useState } from "react";
import { CheckCircle2, XCircle } from "lucide-react";
import { http, fmtNum } from "../../lib/api";

const LABEL = {
  monto_total: "Monto total del crédito", monto_bullet: "Bullet 20%", monto_frances: "Tramo francés 80%",
  aportacion_fondo: "Aportación mensual al fondo", pago_gracia: "Pago meses de gracia", pago_frances: "Pago francés (meses 4–21)",
  total_intereses: "Total intereses", total_iva_intereses: "Total IVA intereses", total_pago_con_iva: "Total pago con IVA",
  tir_nominal: "TIR nominal", tir_efectiva: "TIR efectiva",
};
const fmt = (k, v) => (k.startsWith("tir") ? `${(v * 100).toFixed(2)}%` : fmtNum(v));
const fmtD = (k, v) => (k.startsWith("tir") ? `${(v * 10000).toFixed(1)} pb` : v.toFixed(2));
const Ok = ({ ok, testid }) => (ok
  ? <CheckCircle2 size={16} className="text-emerald-600" data-testid={testid} />
  : <XCircle size={16} className="text-red-600" data-testid={testid} />);

export const AmortizationCheck = () => {
  const [d, setD] = useState(null);
  const [all, setAll] = useState(false);
  useEffect(() => { http.get("/poc/calc/reference").then((r) => setD(r.data)); }, []);
  if (!d) return null;
  const c = d.comparacion;
  const filas = all ? c.filas : c.filas.slice(0, 6).concat(c.filas.slice(-3));
  return (
    <section className="panel p-6 lg:p-8" data-testid="amortization-panel">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="eyebrow">Motor de cálculo</p>
          <h2 className="panel-title mt-1 text-xl sm:text-2xl">Validación del motor</h2>
          <p className="mt-2 text-sm text-zinc-500">Recursos MXN 10,000,000 · 22% fija · 24 meses · gracia 1–3 · 80% francesa · 20% bullet 22–24</p>
        </div>
        <span data-testid="amortization-overall-status" className={`px-3 py-1.5 text-xs font-bold tracking-wider ${c.todo_ok ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>
          {c.todo_ok ? "VALIDADO" : "DIFERENCIAS DETECTADAS"}
        </span>
      </div>
      <div className="mt-6 grid gap-8 lg:grid-cols-12">
        <table className="text-sm lg:col-span-5" data-testid="amortization-key-table">
          <thead><tr className="bg-[#111111] text-left text-[10px] uppercase tracking-wider text-white"><th className="px-3 py-2">Concepto</th><th className="px-3 py-2 text-right">Calculado</th><th className="px-3 py-2 text-right">Referencia</th><th className="px-3 py-2 text-right">Δ</th><th className="px-2" /></tr></thead>
          <tbody>{c.clave.map((k) => (
            <tr key={k.concepto} className="row-hover border-b border-zinc-100" data-testid={`amortization-key-${k.concepto}`}>
              <td className="px-3 py-2">{LABEL[k.concepto]}</td><td className="px-3 py-2 text-right tabular-nums">{fmt(k.concepto, k.calculado)}</td>
              <td className="px-3 py-2 text-right tabular-nums text-zinc-500">{fmt(k.concepto, k.referencia)}</td>
              <td className="px-3 py-2 text-right font-mono text-xs">{fmtD(k.concepto, k.diferencia)}</td><td className="px-2"><Ok ok={k.ok} /></td>
            </tr>))}
          </tbody>
        </table>
        <div className="overflow-x-auto lg:col-span-7">
          <table className="w-full text-xs" data-testid="amortization-verification-table">
            <thead><tr className="bg-[#111111] text-left text-[10px] uppercase tracking-wider text-white">
              {["Mes", "Saldo inicial", "Intereses", "Capital", "Pago", "IVA int.", "Aport. fondo", "Saldo final", "Δ máx", ""].map((h) => <th key={h} className="px-2 py-2 text-right first:text-left">{h}</th>)}
            </tr></thead>
            <tbody>{filas.map((f) => (
              <tr key={f.mes} className="row-hover border-b border-zinc-100 tabular-nums" data-testid={`amortization-row-${f.mes}`}>
                <td className="px-2 py-1.5">{f.mes}</td>
                {["saldo_inicial", "intereses", "capital", "pago", "iva_intereses", "aport_fondo", "saldo_final"].map((k) => <td key={k} className="px-2 py-1.5 text-right">{fmtNum(f.calculado[k])}</td>)}
                <td className="px-2 py-1.5 text-right font-mono">{f.max_diferencia.toFixed(2)}</td>
                <td className="px-2 py-1.5"><Ok ok={f.ok} testid={`amortization-status-badge-${f.mes}`} /></td>
              </tr>))}
            </tbody>
          </table>
          <button data-testid="amortization-toggle-all" onClick={() => setAll(!all)} className="mt-2 text-xs text-orange-600 hover:underline">{all ? "Ver resumen" : "Ver los 24 meses"}</button>
        </div>
      </div>
      <details className="mt-6 text-xs text-zinc-600" data-testid="amortization-notes">
        <summary className="cursor-pointer select-none font-semibold text-[#E8643C]">ⓘ Metodología</summary>
        <ul className="mt-2 space-y-1 border-l-2 border-orange-500 pl-4">{d.notas.map((n) => <li key={n}>{n}</li>)}</ul>
      </details>
    </section>
  );
};
